/**
 * Skrin pilih kasir + PIN.
 *
 * Mengapa ia wujud: `sales.cashier_user_id` sentiasa NULL sebelum ini, jadi pemilik tidak boleh
 * membezakan jualannya daripada jualan pekerja. PIN menyelesaikannya tanpa memberikan kata laluan
 * pemilik kepada kasir.
 *
 * Dua keputusan yang penting:
 *   1. **Tidak menyekat jualan.** Jika tiada kasir didaftarkan, atau peranti offline, skrin ini
 *      boleh dilangkau. Kedai tidak boleh berhenti berjual kerana satu ciri atribusi.
 *   2. **Tidak menyimpan PIN.** PIN dihantar sekali, ditandatangani peranti, kemudian dilupakan.
 */

import { useEffect, useState } from 'react';
import { fetchStaffForDevice, pinLoginForDevice } from '../offline/staff.js';
import type { PosStore } from '../offline/store.js';
import { API_BASE } from '../config.js';

interface Props {
  store: PosStore;
  onDone: (name: string, userId: string) => void;
  onSkip: () => void;
}

export function CashierGate({ store, onDone, onSkip }: Props): React.ReactElement {
  const [staff, setStaff] = useState<Array<{ id: string; name: string }> | null>(null);
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const identity = await store.getIdentity();
      const keys = await store.getKeys();
      if (identity === undefined || keys === undefined) {
        onSkip();
        return;
      }

      try {
        const rows = await fetchStaffForDevice(API_BASE, identity.deviceId, keys.privateKey);
        // Tiada kasir didaftarkan: jangan halang jualan dengan skrin yang tiada pilihan.
        if (rows.length === 0) {
          onSkip();
          return;
        }
        setStaff(rows);
      } catch {
        // Offline atau peranti dicabut: benarkan jualan, atribusi sahaja yang tiada.
        onSkip();
      }
    })();
  }, [store, onSkip]);

  async function submit(): Promise<void> {
    if (selected === null) return;
    setBusy(true);
    setError(null);
    try {
      const identity = await store.getIdentity();
      const keys = await store.getKeys();
      if (identity === undefined || keys === undefined) {
        setError('Peranti ini tiada kunci. Pasangkan semula.');
        return;
      }

      const result = await pinLoginForDevice(API_BASE, identity.deviceId, keys.privateKey, {
        userId: selected.id,
        pin,
      });
      await store.saveCashierToken(result.token);
      onDone(result.name, selected.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'PIN tidak diterima');
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  if (staff === null) {
    return (
      <div className="gate">
        <div className="gate-card">
          <p className="muted">Menyemak kasir…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gate">
      <div className="gate-card">
        <h1>{selected === null ? 'Siapa bekerja?' : selected.name}</h1>

        {selected === null ? (
          <>
            <p className="muted">Pilih nama anda. Jualan akan direkod atas nama ini.</p>
            <div className="gate-staff">
              {staff.map((row) => (
                <button key={row.id} type="button" onClick={() => setSelected(row)}>
                  {row.name}
                </button>
              ))}
            </div>
          </>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <label>
              PIN
              <input
                type="password"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                value={pin}
                onChange={(event) => setPin(event.target.value.replace(/[^0-9]/g, ''))}
              />
            </label>
            {error !== null && <p className="error">{error}</p>}
            <div className="actions">
              <button type="button" className="ghost" onClick={() => setSelected(null)}>
                Tukar nama
              </button>
              <button type="submit" disabled={busy || pin.length < 4}>
                {busy ? 'Menyemak…' : 'Masuk'}
              </button>
            </div>
          </form>
        )}

        <button type="button" className="ghost gate-skip" onClick={onSkip}>
          Teruskan tanpa kasir
        </button>
        <p className="muted small">
          Tanpa kasir, jualan tetap direkod — tetapi tiada nama pada jualan itu.
        </p>
      </div>
    </div>
  );
}
